"use client";

import { useMemo, useState } from "react";
import { useSession } from "next-auth/react";

import { Button } from "~/app/_components/Button";
import { maxQuantityForCartLine, useCart } from "~/app/_components/CartContext";
import type { Product } from "~/data/products";
import { pickBusinessIdForCartLine } from "~/network/customer-products";

function parsePrice(s: string): number {
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

export function AddToCartButton({ product }: { product: Product }) {
  const { status } = useSession();
  const { addItem, addItemAndSyncToServer, openCart, items } = useCart();
  const [isAdding, setIsAdding] = useState(false);

  const baseQtyInCart = useMemo(
    () =>
      items
        .filter((i) => i.productId === product.id && !i.attribute)
        .reduce((sum, i) => sum + i.quantity, 0),
    [items, product.id],
  );

  const max = maxQuantityForCartLine(product.quantity);
  const atMax = max != null && baseQtyInCart >= max;
  const outOfStock = max === 0;

  async function handleClick() {
    if (isAdding) return;

    // product.price is derived from API unit_base_price; treat it as whole units (no cents conversion).
    const unitBasePrice = Math.round(parsePrice(product.price));
    const businessId = pickBusinessIdForCartLine(product);
    const line = {
      productId: product.id,
      name: product.name,
      price: `$${unitBasePrice.toFixed(2)}`,
      image: product.image,
      unitBasePrice,
      ...(businessId ? { businessId } : {}),
      ...(max != null ? { maxQuantity: max } : {}),
    };
    if (status === "authenticated") {
      setIsAdding(true);
      try {
        await addItemAndSyncToServer(line, 1);
      } finally {
        setIsAdding(false);
      }
      openCart();
      return;
    }

    addItem(line, 1);
    openCart();
  }

  return (
    <Button
      type="button"
      onClick={() => void handleClick()}
      loading={isAdding}
      disabled={outOfStock || atMax}
      className="cursor-pointer rounded-lg px-4 py-2 text-sm disabled:cursor-not-allowed"
    >
      {outOfStock ? "Out of stock" : atMax ? "Max in cart" : "Add to Cart"}
    </Button>
  );
}
